/**
 * Inbox context and badges of Core Frontend's messenger (MSG-INBOX-TABS.6).
 *
 * The context (ADR-002 D4) and the tab live in the URL — `?context=merchant:<guid>&tab=…` —
 * so a notification deep-link, «back» and a reload open the same state. Everything here is
 * pure: the page, the header indicator and the realtime sync read the same rules.
 */

import type {
  ConversationListItem,
  ConversationType,
  MerchantUnreadBlock,
  UnreadBlock,
  UnreadCounts,
  ViewerContext,
} from '@/types/messenger';

export const PERSONAL_CONTEXT: ViewerContext = 'personal';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MERCHANT_PREFIX = 'merchant:';

/**
 * The context named by `?context=`. Absent → «Personal» (the default on entering); a value of
 * the wrong grammar → `null`, which the page answers by replacing the URL with «Personal».
 */
export function parseViewerContext(raw: string | null): ViewerContext | null {
  if (raw === null || raw === '' || raw === PERSONAL_CONTEXT) return PERSONAL_CONTEXT;
  if (raw.startsWith(MERCHANT_PREFIX) && UUID_RE.test(raw.slice(MERCHANT_PREFIX.length))) {
    return raw as ViewerContext;
  }
  return null;
}

export function merchantContext(merchantGuid: string): ViewerContext {
  return `${MERCHANT_PREFIX}${merchantGuid}`;
}

/** The shop of a `merchant:<g>` context; `null` for «Personal» */
export function merchantGuidOf(context: ViewerContext): string | null {
  return context.startsWith(MERCHANT_PREFIX) ? context.slice(MERCHANT_PREFIX.length) : null;
}

/**
 * The other side of a thread as the viewer names it — the shop to a buyer, the buyer to a shop.
 * `null` when there is no counterpart (a platform viewer) or it has no name: the caller shows
 * «unknown contact» (`messages.unknown_counterpart`).
 */
export function counterpartName(c: Pick<ConversationListItem, 'counterpart'>): string | null {
  return c.counterpart?.displayName ?? null;
}

/**
 * The shop whose logo stands for the other side: the counterpart when it is a shop; without a
 * counterpart, the shop the thread is about (`contextRef`) — in «Personal» only, where the other
 * side of a support thread is always that shop.
 */
export function counterpartLogoGuid(
  c: Pick<ConversationListItem, 'counterpart' | 'contextRef'>,
  context: ViewerContext
): string | null {
  if (c.counterpart) return c.counterpart.type === 'merchant' ? c.counterpart.guid : null;
  return context === PERSONAL_CONTEXT && c.contextRef?.type === 'merchant' ? c.contextRef.guid : null;
}

// --- Tabs of «Personal» ---

export type InboxTab = 'personal' | 'purchases' | 'listings';

/** In this order (AC-3); «Purchases» is the default and reproduces the buyer's list */
export const INBOX_TABS: readonly InboxTab[] = ['personal', 'purchases', 'listings'];
export const DEFAULT_TAB: InboxTab = 'purchases';

export const TAB_CONVERSATION_TYPE: Record<InboxTab, ConversationType> = {
  personal: 'dm',
  purchases: 'merchant_support',
  listings: 'listing',
};

export function parseInboxTab(raw: string | null): InboxTab {
  return INBOX_TABS.includes(raw as InboxTab) ? (raw as InboxTab) : DEFAULT_TAB;
}

// --- Badges ---

/** A count off the wire, never `NaN` (BUG-MSG-NAN-UNREAD): anything not a finite ≥ 0 number is 0 */
function count(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function normalizeBlock(raw: Partial<UnreadBlock> | null | undefined): UnreadBlock {
  const byType: UnreadBlock['byType'] = {};
  for (const [type, value] of Object.entries(raw?.byType ?? {})) {
    byType[type as ConversationType] = count(value);
  }
  return { byType, total: count(raw?.total) };
}

/**
 * `GET /conversations/unread-counts` as the UI reads it: every number finite, `merchants`
 * always an array. The server's totals are taken as they are — no client-side recount.
 */
export function normalizeUnreadCounts(raw: Partial<UnreadCounts> | null | undefined): UnreadCounts {
  const merchants: MerchantUnreadBlock[] = Array.isArray(raw?.merchants)
    ? raw.merchants
        .filter((m): m is MerchantUnreadBlock => typeof m?.merchantGuid === 'string')
        .map((m) => ({
          ...normalizeBlock(m),
          merchantGuid: m.merchantGuid,
          displayName: m.displayName ?? null,
        }))
    : [];
  return { personal: normalizeBlock(raw?.personal), merchants };
}

/** A tab's badge — `0` when the server left the type out */
export function tabBadge(counts: UnreadCounts | undefined, tab: InboxTab): number {
  return counts?.personal.byType[TAB_CONVERSATION_TYPE[tab]] ?? 0;
}

/** Where the header indicator shows dots (AC-4 table) */
export function headerUnreadDots(counts: UnreadCounts | undefined): {
  personal: boolean;
  shops: boolean;
} {
  return {
    personal: (counts?.personal.total ?? 0) > 0,
    shops: counts?.merchants.some((m) => m.total > 0) ?? false,
  };
}
